import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ArrowRight, Check } from "lucide-react";
import { DocumentArtifactStatus } from "~/config/types";
import { useMyQuizzes } from "~/api/v2/useMyQuizzes";
import { useDocumentReleases } from "~/api/v2/useDocumentReleases";
import { useTags } from "~/api/swr/useTags";
import { type MyQuiz } from "~/api/types";
import { Badge } from "~/atoms/badge";
import { Button } from "~/atoms/button";
import { CenterSpinner } from "~/atoms/spinner";
import ErrorState from "~/molecules/ErrorState";
import { AnchoringTable, type AnchoringColumn } from "./AnchoringTable";
import {
  quizCreatorColumns,
  releaseColumns,
  titleColumn,
  type ReleaseRow,
} from "./cells";

const publishedStatuses = [DocumentArtifactStatus.published];

type MyQuizRow = MyQuiz & ReleaseRow;

export const MyQuizzesTab = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const myQuizzes = useMyQuizzes();
  const releases = useDocumentReleases({ statuses: publishedStatuses });
  const tags = useTags();

  if (myQuizzes.error || releases.error || tags.error) return <ErrorState />;
  if (!myQuizzes.data || !releases.data || !tags.data) return <CenterSpinner />;

  const tagTitleById = new Map(tags.data.map((tag) => [tag.id, tag.title]));
  const releaseById = new Map(
    releases.data.map((release) => [release.id, release]),
  );

  const rows = myQuizzes.data
    .map((quiz): MyQuizRow => {
      const release = releaseById.get(quiz.documentReleaseId);
      // The quiz outlives its release being the published one.
      if (!release) {
        return {
          ...quiz,
          owner: null,
          publisher: null,
          adoptedAt: null,
          tags: [],
        };
      }

      return {
        ...quiz,
        owner: release.document.owner,
        publisher: release.document.publisher,
        adoptedAt: release.adoptedAt,
        tags: release.document.tagIds
          .map((id) => tagTitleById.get(id))
          .filter((title) => title !== undefined),
      };
    })
    .sort((a, b) => Number(a.completed) - Number(b.completed));

  const columns: AnchoringColumn<MyQuizRow>[] = [
    {
      key: "status",
      header: "",
      cell: (row) =>
        row.completed ? (
          <Badge variant="success">
            <Check data-icon="inline-start" />
            {t(($) => $.anchoring.status.completed)}
          </Badge>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => void navigate(`/anchoring/${row.id}`)}
          >
            {t(($) => $.anchoring.status.take)}
            <ArrowRight data-icon="inline-end" />
          </Button>
        ),
    },
    titleColumn(t, "quiz"),
    ...quizCreatorColumns(t),
    ...releaseColumns(t),
  ];

  return (
    <AnchoringTable
      columns={columns}
      rows={rows}
      getRowId={(row) => row.id}
      emptyMessage={t(($) => $.anchoring.empty.mine)}
    />
  );
};
